// src/pages/Appli/AppliAdmin/AlbumsPhotos/CreateAlbumModal.jsx
import React, { useState, useContext, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaTimes, FaSave } from "react-icons/fa";

import Button from "../../../../components/Button/Button";
import { LanguageContext } from "../../../../contexts/LanguageContext";

const CreateAlbumModal = ({ onClose, onCreate }) => {
  const { language } = useContext(LanguageContext);
  const [localLanguage, setLocalLanguage] = useState(language || "fr");

  const lang = useMemo(() => {
    const v = String(localLanguage).toLowerCase();
    if (v.startsWith("en")) return "en";
    return "fr";
  }, [localLanguage]);

  const [formData, setFormData] = useState({
    titre_fr: "",
    titre_en: "",
    description_fr: "",
    description_en: "",
    // kanji existe en DB mais on ne l'utilise pas en UI
    kanji: "",
    is_active: true,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const v = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: v }));
  };

  const handleContentChange = (value) => {
    setFormData((prev) => ({ ...prev, [`description_${lang}`]: value }));
  };

  const handleCreate = async () => {
    const payload = {
      ...formData,
      titre_fr: String(formData.titre_fr || "").trim(),
      titre_en: String(formData.titre_en || "").trim(),
      description_fr: String(formData.description_fr || "").trim(),
      description_en: String(formData.description_en || "").trim(),
      is_active: !!formData.is_active,
      // kanji laissé tel quel ("" par défaut)
    };

    await onCreate(payload);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl rounded-3xl border border-white/10 bg-white dark:bg-gray-900 shadow-2xl overflow-hidden">
        <div className="bg-primary text-white px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-extrabold">Créer un album</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl px-3 py-2 bg-white/15 hover:bg-white/20 transition"
            title="Fermer"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-6 max-h-[80vh] overflow-y-auto space-y-5">
          {/* Lang */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1">
              Langue d’édition
            </label>
            <select
              value={lang}
              onChange={(e) => setLocalLanguage(e.target.value)}
              className="w-full rounded-2xl px-3 py-2 text-sm border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 text-gray-900 dark:text-white outline-none"
            >
              <option value="fr">FR</option>
              <option value="en">EN</option>
            </select>
          </div>

          {/* Titre */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Titre ({lang.toUpperCase()})
            </label>
            <input
              type="text"
              name={`titre_${lang}`}
              value={formData[`titre_${lang}`] || ""}
              onChange={handleChange}
              className="w-full rounded-2xl px-3 py-2 text-sm border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/20 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary"
              placeholder={`Titre (${lang.toUpperCase()})`}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
              Description ({lang.toUpperCase()})
            </label>
            <div className="rounded-3xl border border-black/10 dark:border-white/10 overflow-hidden bg-white dark:bg-black/20">
              <ReactQuill
                value={formData[`description_${lang}`] || ""}
                onChange={handleContentChange}
              />
            </div>
          </div>

          {/* Actif */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              name="is_active"
              checked={!!formData.is_active}
              onChange={handleChange}
              className="h-5 w-5 accent-primary"
            />
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Album actif
            </span>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-black/10 dark:border-white/10 flex justify-end gap-3">
          <Button text="Annuler" onClick={onClose} icon={FaTimes} className="gap-2" />
          <Button text="Créer" onClick={handleCreate} icon={FaSave} className="gap-2" />
        </div>
      </div>
    </div>
  );
};

export default CreateAlbumModal;
